import { useEffect, useRef, useState } from "react";
import { ArrowRight, LogOut } from "lucide-react";
import { AuthLayout } from "../components/AuthLayout";
import { Button } from "../components/Button";
import { LoadingScreen } from "../components/LoadingScreen";
import { MessageBanner } from "../components/MessageBanner";
import { acceptInvite, fetchInviteValidation } from "../lib/api/endpoints";
import { ApiError } from "../lib/api/client";
import { INVITE_STATUS, inviteStatusLabel } from "../lib/enums";
import { useSession } from "../lib/session/SessionContext";
import { LoginScreen } from "./LoginScreen";
import { SignupScreen } from "./SignupScreen";
import type { InviteValidationDto } from "../types";

interface InviteFlowProps {
  readonly code: string;
  readonly onComplete: () => void;
}

type Validation =
  | { kind: "loading" }
  | { kind: "unavailable"; message: string }
  | { kind: "error"; message: string }
  | { kind: "ready"; invite: InviteValidationDto };

const ONBOARDING_DATA_REQUIRED = "INVITE_ONBOARDING_DATA_REQUIRED";

export function InviteFlow({ code, onComplete }: InviteFlowProps) {
  const { status, accessToken, refreshTenants, logout } = useSession();
  const [validation, setValidation] = useState<Validation>({ kind: "loading" });
  const [mode, setMode] = useState<"signup" | "login">("signup");
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const probedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const invite = await fetchInviteValidation(code);
        if (cancelled) return;
        if (invite.status === INVITE_STATUS.Pending) {
          setValidation({ kind: "ready", invite });
        } else {
          setValidation({ kind: "unavailable", message: `Este convite ${inviteStatusLabel(invite.status)}.` });
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setValidation({ kind: "unavailable", message: "Convite não encontrado. Verifique o link recebido." });
        } else {
          setValidation({ kind: "error", message: err instanceof Error ? err.message : "Não foi possível validar o convite." });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  useEffect(() => {
    if (status !== "authed" || !accessToken || validation.kind !== "ready" || probedRef.current) return;
    probedRef.current = true;
    setAccepting(true);
    setAcceptError(null);
    void (async () => {
      try {
        // Convites de equipe (professor) aceitam direto com corpo vazio.
        const result = await acceptInvite(accessToken, code, {});
        await refreshTenants(result.tenantId);
        onComplete();
      } catch (err) {
        setAcceptError(describeAcceptError(err));
      } finally {
        setAccepting(false);
      }
    })();
  }, [status, accessToken, validation, code, refreshTenants, onComplete]);

  if (validation.kind === "loading") {
    return <LoadingScreen label="Validando convite..." />;
  }

  if (validation.kind === "unavailable") {
    return (
      <AuthLayout
        title="Convite indisponível"
        subtitle={validation.message}
        footer={<span>Apogeu App · alunos e professores</span>}
      >
        <Button fullWidth onClick={onComplete}>
          <ArrowRight size={17} aria-hidden="true" />
          Ir para o app
        </Button>
      </AuthLayout>
    );
  }

  if (validation.kind === "error") {
    return (
      <AuthLayout
        title="Não foi possível validar"
        subtitle={validation.message}
        footer={<span>Apogeu App · alunos e professores</span>}
      >
        <Button fullWidth onClick={() => window.location.reload()}>Tentar novamente</Button>
      </AuthLayout>
    );
  }

  if (accepting) {
    return <LoadingScreen label="Liberando seu acesso..." />;
  }

  if (acceptError) {
    return (
      <AuthLayout
        title="Convite não aceito"
        subtitle="Não conseguimos vincular este convite à sua conta."
        footer={<span>Apogeu App · alunos e professores</span>}
      >
        <MessageBanner>{acceptError}</MessageBanner>
        <Button
          fullWidth
          onClick={() => {
            probedRef.current = false;
            setAcceptError(null);
          }}
        >
          Tentar novamente
        </Button>
        <Button
          variant="ghost"
          fullWidth
          onClick={() => {
            void logout();
            setMode("login");
            probedRef.current = false;
            setAcceptError(null);
          }}
        >
          <LogOut size={16} aria-hidden="true" />
          Entrar com outra conta
        </Button>
      </AuthLayout>
    );
  }

  if (status === "anon") {
    if (mode === "login") return <LoginScreen />;
    return <SignupScreen invitedEmail={validation.invite.email} onUseExistingAccount={() => setMode("login")} />;
  }

  return <LoadingScreen label="Liberando seu acesso..." />;
}

function describeAcceptError(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : "Não foi possível aceitar o convite.";
  }

  const code = err.extensions?.code;

  if (err.status === 400 && code === ONBOARDING_DATA_REQUIRED) {
    return "Este convite é de gestão do CT. Continue o onboarding no Apogeu OS.";
  }
  if (err.status === 409 && code === "INVITE_NOT_PENDING") {
    return "Este convite já foi usado ou não está mais pendente.";
  }
  if (err.status === 409 && code === "MEMBER_ALREADY_EXISTS") {
    return "Sua conta já faz parte deste CT. Volte para o app.";
  }
  if (err.status === 403 && code === "INVITE_EMAIL_MISMATCH") {
    return "O e-mail da sua conta não corresponde ao e-mail convidado.";
  }
  return err.detail ?? "Não foi possível aceitar o convite.";
}
